// import {FeatureToggle} from '@/config/featureToggles'
// import {isFeatureEnabled} from '@/helpers/featureToggle'
import {InputSelector} from '@/shared-components/InputSelector/InputSelector'
import {RemoveParameterButton} from '@/shared-components/RemoveParameterButton'
import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {CategoryType, DataParams, Operators, ParamsSelector} from '../../../types/paramFilters/paramFilters'
import {TraitCategories} from '../../../types/paramFilters/traitCategories'
import {ServerValueType} from '../../../types/paramFilters/paramFilters'
import {findTrait, getOperatorCode, getOperatorType} from '../../../utils/traitCategoryUtils'
import {ChangeFilterValueProps, FilterOptionsByParam} from './FilterOptionsByParam'

const getTraitOptionsFromCategories = (traitCategories: TraitCategories | undefined, categoryCode: string) =>
  traitCategories
    ?.find(categoryItem => categoryItem.code === categoryCode)
    ?.traits.map(trait => ({
      value: trait.code as DataParams,
      displayValue: trait.name,
      type: getOperatorType(trait.operator[0]),
    })) ?? []

type ServerTraitOption = {
  value: DataParams
  displayValue: string
  type: ServerValueType
}

interface Props {
  position: number
  isPublished: boolean
  hasMultipleParamsSelectors: boolean
  currentParamSelector: ParamsSelector
  updateSelectedParam: (id: number, updatedParamSelector: ParamsSelector | null) => void
  assignedSegmentList?: string[]
  traitCategories?: TraitCategories
}

export const ParamSelector: React.FC<Props> = ({
  position,
  isPublished,
  currentParamSelector,
  hasMultipleParamsSelectors,
  updateSelectedParam,
  assignedSegmentList,
  traitCategories,
}) => {
  const {selectedCategory: tempSelectedCategory, selectedParam, id} = currentParamSelector
  const [selectedCategory, setSelectedCategory] = useState<string>(tempSelectedCategory)
  const [traitList, setTraitList] = useState<ServerTraitOption[]>(() =>
    getTraitOptionsFromCategories(traitCategories, tempSelectedCategory)
  )
  const isFirstParamSelector = position === 0

  const shouldDisplayRemoveButton =
    !isPublished && (hasMultipleParamsSelectors || (isFirstParamSelector && selectedParam !== ''))

  const {formatMessage} = useIntl()

  useEffect(() => {
    if (!traitCategories) return

    setTraitList(getTraitOptionsFromCategories(traitCategories, selectedCategory))
  }, [selectedCategory, traitCategories])

  const onSelectNewCategory = (newCategory: CategoryType) => {
    if (newCategory === '') {
      return
    }
    setSelectedCategory(newCategory)
    const updatedSelectedParam = {...currentParamSelector, selectedCategory: newCategory}
    updateSelectedParam(id, updatedSelectedParam)
    const newOptions = getTraitOptionsFromCategories(traitCategories, newCategory)
    setTraitList(newOptions)
    if (!newOptions.length) return
    onSelectNewParam(newOptions[0].value, updatedSelectedParam)
  }

  const onSelectNewParam = (newParam: DataParams, updatedCategoryParamSelector?: ParamsSelector) => {
    if (isPublished) return

    const selectedTrait = findTrait(
      traitCategories,
      updatedCategoryParamSelector?.selectedCategory ?? selectedCategory,
      newParam
    )
    if (!selectedTrait) return

    const selectedType = {type: getOperatorType(selectedTrait.operator[0])}
    const firstOperator = getOperatorCode(selectedTrait.operator[0].code)

    const tempParamsSelectors = updatedCategoryParamSelector ?? currentParamSelector
    const updatedSelectedParam: ParamsSelector = {
      ...tempParamsSelectors,
      selectedParamType: selectedType.type,
      selectedParam: newParam,
      selectedParamData: {
        operator: firstOperator,
        value: null,
      },
    }

    updateSelectedParam(id, updatedSelectedParam)
  }

  const shouldSetOperatorValueToNull = (newOperator: Operators, oldOperator: Operators) => {
    const newOperatorMetadata = findTrait(
      traitCategories,
      currentParamSelector.selectedCategory,
      currentParamSelector.selectedParam
    )?.operator.find(operator => getOperatorCode(operator.code) === newOperator)
    const oldOperatorMetadata = findTrait(
      traitCategories,
      currentParamSelector.selectedCategory,
      currentParamSelector.selectedParam
    )?.operator.find(operator => getOperatorCode(operator.code) === oldOperator)
    const isListValue = (valueType?: string) => valueType === 'textarea' || valueType === 'multipleSelect'

    return (
      newOperatorMetadata?.value === null ||
      (isListValue(newOperatorMetadata?.value?.valueType) && !isListValue(oldOperatorMetadata?.value?.valueType)) ||
      (!isListValue(newOperatorMetadata?.value?.valueType) && isListValue(oldOperatorMetadata?.value?.valueType))
    )
  }

  const onChangeFilterValue = ({key, newValue}: ChangeFilterValueProps) => {
    if (!selectedParam || isPublished) return

    const updatedSelectedParam = {
      ...currentParamSelector,
      selectedParamData: {...currentParamSelector.selectedParamData, [key]: newValue},
    } as ParamsSelector

    if (key === 'operator') {
      const oldOperator = currentParamSelector.selectedParamData.operator

      if (shouldSetOperatorValueToNull(newValue, oldOperator)) {
        updatedSelectedParam.selectedParamData.value = null
      }
    }

    if (key === 'value' && newValue === '') {
      updatedSelectedParam.selectedParamData.value = null
    }

    updateSelectedParam(id, updatedSelectedParam)
  }

  const removeCurrentSelector = () => {
    updateSelectedParam(id, null)
  }

  const category = useMemo(() => {
    return (
      (traitCategories ?? [])
        .filter(categoryItem => categoryItem.active)
        // Frontend category feature toggles are temporarily disabled.
        // The trait-categories API is the source of truth for category visibility.
        // .filter(categoryItem => {
        //   if (['order'].includes(categoryItem.code)) {
        //     return isFeatureEnabled(FeatureToggle.traitsOrders)
        //   }
        //   if (['points'].includes(categoryItem.code)) {
        //     return isFeatureEnabled(FeatureToggle.traitsPoints)
        //   }
        //   if (['promotion'].includes(categoryItem.code)) {
        //     return isFeatureEnabled(FeatureToggle.traitsPromotions)
        //   }
        //   if (['target', 'campaign'].includes(categoryItem.code)) {
        //     return isFeatureEnabled(FeatureToggle.traitsTargets)
        //   }
        //   return true
        // })
        .sort((a, b) => a.name.localeCompare(b.name))
    )
  }, [traitCategories])

  return (
    <>
      <div className="mb-4 flex gap-4 text-sm font-bold leading-6">
        {formatMessage({id: 'ENTRY_CONDITION_CONTENT.PARAMETER'}, {position: position + 1})}
        {shouldDisplayRemoveButton && <RemoveParameterButton iconSize="small" onClick={removeCurrentSelector} />}
      </div>
      <div className="relative flex w-full flex-wrap items-start justify-between gap-4">
        <div className="flex w-full">
          <div className="flex w-full max-w-[301px] flex-col gap-2">
            <InputSelector
              id="category"
              label={formatMessage({id: 'ENTRY_CONDITION_CONTENT.PARAMETER_CONDITION_CATEGORY'})}
              onChange={evt => onSelectNewCategory(evt.target.value as CategoryType)}
              value={selectedCategory}
              disabled={isPublished}
              required
            >
              <option hidden>{formatMessage({id: 'ENTRY_CONDITION_CONTENT.SELECT_A_CATEGORY'})}</option>
              {category.map(categoryItem => {
                return (
                  <option value={categoryItem.code} key={categoryItem.code} data-testid="category-option">
                    {categoryItem.name}
                  </option>
                )
              })}
            </InputSelector>
          </div>
        </div>

        <div className="flex w-full max-w-[301px] flex-col gap-2">
          <InputSelector
            id="param"
            label={formatMessage({id: 'ENTRY_CONDITION_CONTENT.PARAMETER_CONDITION_TRAIT'})}
            onChange={evt => onSelectNewParam(evt.target.value as DataParams)}
            value={selectedParam}
            disabled={isPublished}
            required
          >
            <option hidden>{formatMessage({id: 'ENTRY_CONDITION_CONTENT.SELECT_A_PARAMETER'})}</option>
            {traitList.map(({value, displayValue}) => {
              return (
                <option value={value} key={value} data-testid="param-option">
                  {traitCategories ? displayValue : formatMessage({id: displayValue})}
                </option>
              )
            })}
          </InputSelector>
        </div>

        <FilterOptionsByParam
          currentParamSelector={currentParamSelector}
          onChangeFilterValue={onChangeFilterValue}
          disabled={isPublished}
          assignedSegmentList={assignedSegmentList}
          traitCategories={traitCategories}
        />
      </div>
    </>
  )
}
